import type { RelationshipSupport } from '../types/relationships';

/** One kind of signal between two players, with everything the report says about it. */
export interface SignalKindSummary {
  kind: string;
  family: string;
  count: number;
  weight: number;
  /** This kind's share of the total weight, 0 to 1. */
  share: number;
  first: string;
  last: string;
  /** Distinct source records behind this kind, most recent first. */
  records: string[];
}

export interface SignalReport {
  total: number;
  weight: number;
  first: string | null;
  last: string | null;
  activeDays: number;
  busiest: { day: string; count: number } | null;
  /** Heaviest kind first. */
  kinds: SignalKindSummary[];
}

/** Folds a connection's retained signals into the compact report the inspector shows instead of a record-by-record list. */
export function summarizeSignals(support: RelationshipSupport[]): SignalReport {
  const kinds = new Map<string, SignalKindSummary>(), days = new Map<string, number>();
  let weight = 0, first: string | null = null, last: string | null = null;
  for (const signal of support) {
    const value = Number.isFinite(signal.weight) ? Math.max(0, signal.weight) : 0;
    weight += value;
    if (!first || signal.at < first) first = signal.at;
    if (!last || signal.at > last) last = signal.at;
    const day = signal.at.slice(0, 10);
    days.set(day, (days.get(day) ?? 0) + 1);
    const key = `${signal.family}|${signal.kind}`;
    const entry = kinds.get(key) ?? { kind: signal.kind, family: signal.family, count: 0, weight: 0, share: 0, first: signal.at, last: signal.at, records: [] };
    entry.count++;
    entry.weight += value;
    if (signal.at < entry.first) entry.first = signal.at;
    if (signal.at > entry.last) entry.last = signal.at;
    for (const id of signal.evidence) if (!entry.records.includes(id)) entry.records.push(id);
    kinds.set(key, entry);
  }
  let busiest: SignalReport['busiest'] = null;
  for (const [day, count] of days) if (!busiest || count > busiest.count || (count === busiest.count && day > busiest.day)) busiest = { day, count };
  const list = [...kinds.values()].map(entry => ({ ...entry, share: weight > 0 ? entry.weight / weight : 0 }))
    .sort((a, b) => b.weight - a.weight || b.count - a.count || a.kind.localeCompare(b.kind));
  return { total: support.length, weight, first, last, activeDays: days.size, busiest, kinds: list };
}
